<template>
  <v-overlay v-if="visible" :dark="false" opacity="0.92" z-index="300" class="skin-reveal-overlay">
    <div class="reveal-dialog">
      <div class="reveal-header">
        <div class="reveal-title">
          {{ $t('skinReveal.title', { category: categoryLabel }) }}
        </div>
      </div>

      <!-- Step 1: pick one of three face-down cards -->
      <div v-if="step === 'PICKING'" class="reveal-step reveal-step-picking">
        <div class="reveal-subtitle">{{ $t('skinReveal.chooseDescription') }}</div>
        <div class="cards-row">
          <button
            v-for="i in 3"
            :key="i"
            class="facedown-card"
            :class="{ 'is-picked': pickedIndex === i - 1 }"
            :disabled="pickedIndex !== null"
            @click="pickCard(i - 1)"
          >
            <img :src="facedownImage" :alt="$t('skinReveal.hiddenCardAlt')" />
          </button>
        </div>
      </div>

      <!-- Step 2: revealed character (just the shield, no description) -->
      <div v-else-if="step === 'RESULT'" class="reveal-step reveal-step-result">
        <div class="reveal-character">
          <SkinnedTournamentOverviewCard
            :participant-data="participantData"
            :participants="[participantData]"
            :theme-code-override="themeCode"
            :awards-override="[]"
            :should-show-awards="false"
            :should-show-sponsor="false"
            :preview-mode="true"
            ranking-type="BALANCE"
          />
        </div>
        <button class="go-to-competition-btn" :style="ctaButtonStyle" @click="handleClose">
          {{ $t('skinReveal.goToCompetition') }}
        </button>
      </div>
    </div>
  </v-overlay>
</template>

<script>
import facedownCards from '@/assets/skins_assets/facedown-cards';
import SkinnedTournamentOverviewCard from '@/components/SkinnedTournamentOverviewCard.vue';

export default {
  name: 'SkinRevealDialog',
  components: { SkinnedTournamentOverviewCard },
  props: {
    visible: { type: Boolean, default: false },
    participantData: { type: Object, default: () => ({}) },
    themeCode: { type: String, default: null },
    categoryDisplayName: { type: String, default: '' },
  },
  data() {
    return {
      step: 'PICKING',
      pickedIndex: null,
    };
  },
  computed: {
    facedownImage() {
      if (!this.themeCode) return null;
      const key = String(this.themeCode).toUpperCase();
      return facedownCards[key] || facedownCards.WC26;
    },
    categoryLabel() {
      const name = this.categoryDisplayName || this.themeCode || '';
      const s = String(name).trim();
      if (!s) return '';
      if (s.length <= 3) return s;
      if (/^[A-Z]+$/.test(s)) return s.charAt(0) + s.slice(1).toLowerCase();
      return s;
    },
    ctaButtonStyle() {
      // Match the fallback chain used elsewhere in the tournament overview:
      // 1. tournament.tournamentSettings.colors (primary, dark-mode swaps to secondary)
      // 2. clubMember.club.primaryColor
      // 3. keep the default .go-to-competition-btn red via {}
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      if (colors && (colors.primary || colors.secondary)) {
        const isDark = this.$vuetify?.theme?.dark;
        const chosen = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (chosen) return this.colorStyleFor(chosen);
      }
      const clubColor = this.$store.state.clubMember?.club?.primaryColor;
      if (clubColor) return this.colorStyleFor(clubColor);
      return {};
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.step = 'PICKING';
        this.pickedIndex = null;
      }
    },
  },
  methods: {
    pickCard(index) {
      if (this.pickedIndex !== null) return;
      this.pickedIndex = index;
      // Tiny pause so the "is-picked" animation can play before we swap steps.
      setTimeout(() => {
        this.step = 'RESULT';
      }, 550);
    },
    handleClose() {
      this.$emit('close');
    },
    colorStyleFor(raw) {
      const clean = String(raw).replace('#', '');
      const full = `#${clean}`;
      return {
        backgroundColor: full,
        borderColor: full,
        color: this.getContrastYIQ(clean) === 'black' ? '#000' : '#fff',
      };
    },
    getContrastYIQ(hex) {
      if (!hex) return 'black';
      const cleaned = String(hex).replace('#', '');
      if (cleaned.length < 6) return 'black';
      const r = parseInt(cleaned.substr(0, 2), 16);
      const g = parseInt(cleaned.substr(2, 2), 16);
      const b = parseInt(cleaned.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return yiq >= 128 ? 'black' : 'white';
    },
  },
};
</script>

<style lang="scss" scoped>
.skin-reveal-overlay {
  ::v-deep .v-overlay__content {
    width: 100%;
    max-width: 640px;
    padding: 0 16px;
  }
}

.reveal-dialog {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Avenir', sans-serif;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  color: #1b1b1b;
}

.reveal-header {
  padding: 18px 24px;
  text-align: center;
  border-bottom: 1px solid #ededed;

  .reveal-title {
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #1b1b1b;
  }
}

.reveal-step {
  padding: 28px 24px 32px;
}

.reveal-subtitle {
  text-align: center;
  color: #5a5a5a;
  font-size: 14px;
  margin-bottom: 24px;
}

.cards-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;

  @media (max-width: 600px) {
    gap: 10px;
  }
}

.facedown-card {
  position: relative;
  width: 180px;
  height: 260px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 0.35s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    width: 105px;
    height: 160px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.35s ease, filter 0.35s ease;
    transform-origin: center center;
    // drop-shadow follows the PNG's alpha channel so the shadow hugs the
    // actual card silhouette instead of the rectangular wrapper.
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.2));
  }

  &:hover:not(:disabled) img {
    transform: translateY(-8px) scale(1.04);
    filter: drop-shadow(0 12px 18px rgba(0, 0, 0, 0.35));
  }

  &:disabled {
    cursor: default;

    &:not(.is-picked) {
      opacity: 0.35;
    }
  }

  &.is-picked img {
    animation: pickedPulse 0.55s ease-out;
  }
}

@keyframes pickedPulse {
  0%   { transform: translateY(-12px) scale(1.05) rotateY(0deg);   filter: drop-shadow(0 14px 22px rgba(0, 0, 0, 0.4)); }
  50%  { transform: translateY(-12px) scale(1.10) rotateY(180deg); filter: drop-shadow(0 18px 26px rgba(0, 0, 0, 0.45)); }
  100% { transform: translateY(-12px) scale(1.05) rotateY(360deg); filter: drop-shadow(0 14px 22px rgba(0, 0, 0, 0.4)); }
}

.reveal-step-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  .reveal-character {
    width: 260px;
    max-width: 100%;
    pointer-events: none;
    user-select: none;
    animation: cardReveal 0.5s ease-out;
  }
}

@keyframes cardReveal {
  from { opacity: 0; transform: translateY(10px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.go-to-competition-btn {
  background-color: #c03131;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #a52424;
  }
}
</style>
